import {
  type CSSProperties,
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors } from "@rubi/ui";
import { GoogleMapsProvider } from "@rubi/ui";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";

import { defaultTheme } from "@/components/online-reporting/constants";

import { parseAamva } from "./aamva-parser";
import { zxingDecoder } from "./decoder";
import { reverseGeocode } from "./geocode";
import { initialScannerState, scannerReducer } from "./scannerReducer";
import { VIEWFINDER } from "./scanRegion";
import type {
  Decoder,
  IPlace,
  ParsedLicense,
  PermissionFailureReason,
  ScannerState,
} from "./types";

const LOCK_ON_MS = 600;
const STUCK_HINT_MS = 8000;

// SurveyJS applies the portal theme's CSS variables (primary color var(--info-dark),
// 4px corner radius, etc.) to the survey's root container. This modal is a MUI
// Dialog that portals to <body> — outside that container — so the variables
// don't reach it. Re-declaring them on the modal root lets the native `sd-btn`
// classes resolve to the same colors and radius as the survey chrome (and the
// scan-trigger button), instead of falling back to the library's teal default.
const sjsThemeVars = defaultTheme.cssVariables as CSSProperties;

// `--font-family` is defined globally on :root by survey-core, but MUI's
// Typography hard-codes its own font. Point Typography at the SurveyJS font so
// the modal's prompts read as survey copy rather than default Material text.
const surveyFont = "var(--sjs-font-family, var(--font-family))";

export interface ScannerModalProps {
  open: boolean;
  // Called the instant the user confirms. The address geocode is handed off
  // as a still-pending promise so the consumer can fill the scalar fields now
  // and the address later — confirming never blocks on Google.
  onResult: (
    parsed: ParsedLicense,
    placePromise: Promise<IPlace | null>,
  ) => void;
  onCancel: () => void;

  // Story / test overrides (production code never passes these):
  decoder?: Decoder;
  videoSlot?: ReactNode;
  initialState?: ScannerState;
}

const isMediaSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  typeof navigator.mediaDevices?.getUserMedia === "function";

const classifyMediaError = (err: unknown): PermissionFailureReason => {
  if (typeof err !== "object" || err === null) return "other";
  const name = (err as { name?: string }).name ?? "";
  if (name === "NotAllowedError" || name === "SecurityError") return "denied";
  if (name === "NotFoundError" || name === "OverconstrainedError")
    return "not-found";
  return "other";
};

export const ScannerModal: FC<ScannerModalProps> = (props) => {
  return (
    <GoogleMapsProvider>
      <ScannerModalInner {...props} />
    </GoogleMapsProvider>
  );
};

const ScannerModalInner: FC<ScannerModalProps> = ({
  open,
  onResult,
  onCancel,
  decoder = zxingDecoder,
  videoSlot,
  initialState,
}) => {
  const { t } = useTranslation("customQuestions");
  const tm = (k: string, o?: Record<string, unknown>) =>
    t(`driversLicenseScanner.modal.${k}`, o);
  const [state, dispatch] = useReducer(
    scannerReducer,
    initialState ?? initialScannerState,
  );
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const resultHandledRef = useRef(false);
  const geocodePromiseRef = useRef<Promise<IPlace | null> | null>(null);
  const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stuckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set true by handleCancel and by unmount — guards async completions
  // (geocode await) from calling onResult or setState after the user has
  // closed the modal.
  const dismissedRef = useRef(false);
  useEffect(() => {
    return () => {
      dismissedRef.current = true;
    };
  }, []);

  const geocodingLib = useMapsLibrary("geocoding");
  const geocoder = useMemo(
    () => (geocodingLib ? new geocodingLib.Geocoder() : null),
    [geocodingLib],
  );

  // 1. Camera permission on open
  useEffect(() => {
    if (!open) return;
    if (initialState) {
      // Stories drive state directly; skip permission flow.
      return;
    }
    if (!isMediaSupported()) {
      dispatch({ type: "PERMISSION_DENIED", reason: "unsupported" });
      return;
    }

    let cancelled = false;
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: "environment",
          // Dense AAMVA PDF417 needs ~2-3 px per module to decode. Without a
          // resolution hint browsers default to ~640x480, which is too coarse
          // to resolve a full driver's-license barcode. Request high res
          // (ideal, so it degrades gracefully) and continuous autofocus —
          // focus matters as much as resolution for dense barcodes.
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          // @ts-expect-error focusMode is non-standard but widely supported on mobile
          focusMode: "continuous",
        },
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        dispatch({ type: "PERMISSION_GRANTED" });
      })
      .catch((err) => {
        if (cancelled) return;
        dispatch({
          type: "PERMISSION_DENIED",
          reason: classifyMediaError(err),
        });
      });

    return () => {
      cancelled = true;
      // Stop the camera tracks here too — this cleanup runs both when
      // `open` flips false AND when the component unmounts (the parent
      // conditionally mounts us, so unmount is the usual close path).
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [open, initialState]);

  // 2. Start decoder when entering `searching`
  useEffect(() => {
    if (state.kind !== "searching") return;
    if (!videoRef.current) return;

    const video = videoRef.current;
    resultHandledRef.current = false;
    let cancelled = false;

    const beginDecode = () => {
      if (cancelled) return;
      decoder
        .start(video, (result) => {
          if (cancelled) return;
          if (resultHandledRef.current) return;
          resultHandledRef.current = true;
          dispatch({
            type: "DECODE_SUCCESS",
            raw: result.raw,
            points: result.points,
          });
        })
        .catch((err) => {
          if (cancelled) return;

          console.warn("[scanner] decoder failed to start:", err);
          dispatch({ type: "PERMISSION_DENIED", reason: "other" });
        });
    };

    // The video is paused on the matched frame during `locked-on` (effect 3).
    // On "try again" it is still frozen on the previous license, so starting
    // the decoder immediately would read that stale frame and instantly
    // re-detect the same barcode even when the camera has moved away. Resume
    // playback and wait for one fresh frame before decoding.
    video.play().catch(() => {});
    const v = video as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number;
    };
    if (typeof v.requestVideoFrameCallback === "function") {
      v.requestVideoFrameCallback(() => beginDecode());
    } else {
      beginDecode();
    }

    stuckTimerRef.current = setTimeout(() => {
      dispatch({ type: "STUCK_HINT_ELAPSED" });
    }, STUCK_HINT_MS);

    return () => {
      cancelled = true;
      decoder.stop();
      if (stuckTimerRef.current) clearTimeout(stuckTimerRef.current);
    };
  }, [state.kind, decoder]);

  // 3. Lock-on timer + freeze video
  useEffect(() => {
    if (state.kind !== "locked-on") return;
    if (videoRef.current) videoRef.current.pause();

    lockTimerRef.current = setTimeout(() => {
      dispatch({ type: "LOCK_TIMER_ELAPSED" });
    }, LOCK_ON_MS);

    return () => {
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    };
  }, [state.kind]);

  // 4. Parse and kick off geocode. Dependency is narrowed to the raw
  //    payload (only changes when transitioning into `parsing`) so we
  //    don't re-run after each transition out of parsing. Without this,
  //    React Strict Mode double-invokes the effect and parseAamva runs
  //    twice per scan.
  const parsingRaw = state.kind === "parsing" ? state.raw : null;
  useEffect(() => {
    if (parsingRaw === null) return;

    const parsed = parseAamva(parsingRaw);
    if (!parsed || isParsedEmpty(parsed)) {
      dispatch({ type: "PARSE_FAIL" });
      return;
    }

    if (parsed.address && geocoder) {
      const addressString = formatAddress(parsed.address);
      geocodePromiseRef.current = reverseGeocode(geocoder, addressString);
    } else {
      // Geocoder not ready yet (Maps lib still loading). Leave the promise
      // unset; handleDone starts the geocode if the lib has loaded by the time
      // the user confirms.
      geocodePromiseRef.current = null;
    }

    dispatch({ type: "PARSE_OK", parsed });
  }, [parsingRaw, geocoder]);

  const handleDone = useCallback(() => {
    if (state.kind !== "success") return;
    // Confirm immediately and hand off the in-flight reverse-geocode. The
    // consumer applies the scalar fields now and fills the address later, when
    // (and if) the geocode resolves — confirming never blocks on Google. The
    // geocode request was dispatched at parse time and resolves independently
    // of this modal's lifecycle, so it survives the modal closing.
    let placePromise = geocodePromiseRef.current;
    // If the geocoder wasn't ready at parse time but has since loaded, kick off
    // the geocode now so the address still fills in the background.
    if (!placePromise && state.parsed.address && geocoder) {
      placePromise = reverseGeocode(
        geocoder,
        formatAddress(state.parsed.address),
      );
    }
    onResult(state.parsed, placePromise ?? Promise.resolve(null));
  }, [state, onResult, geocoder]);

  const handleCancel = useCallback(() => {
    dismissedRef.current = true;
    decoder.stop();
    dispatch({ type: "CANCEL" });
    onCancel();
  }, [decoder, onCancel]);

  return (
    <Dialog
      open={open}
      fullScreen
      onClose={handleCancel}
      data-testid="scanner-modal"
    >
      <Box
        style={sjsThemeVars}
        sx={{
          position: "relative",
          height: "100%",
          bgcolor: colors.common.black,
          color: colors.common.white,
          "& .MuiTypography-root": { fontFamily: surveyFont },
        }}
      >
        <IconButton
          onClick={handleCancel}
          aria-label={tm("cancelScan")}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 10,
            color: colors.common.white,
            bgcolor: alpha(colors.common.black, 0.4),
          }}
        >
          <CloseIcon />
        </IconButton>

        {videoSlot ?? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}

        <ReticleOverlay />

        {state.kind === "requesting-permission" && (
          <CenterOverlay>
            <CircularProgress sx={{ color: colors.common.white }} />
            <Typography sx={{ mt: 2 }}>{tm("startingCamera")}</Typography>
          </CenterOverlay>
        )}

        {state.kind === "searching" && state.stuckHintShown && (
          <TopBanner>{tm("stuckHint")}</TopBanner>
        )}

        {state.kind === "locked-on" && (
          <LockOnOverlay
            points={state.points}
            videoNaturalWidth={videoRef.current?.videoWidth}
            videoNaturalHeight={videoRef.current?.videoHeight}
          />
        )}

        {state.kind === "parsing" && (
          <CenterOverlay>
            <CircularProgress sx={{ color: colors.common.white }} />
            <Typography sx={{ mt: 2 }}>{tm("readingBarcode")}</Typography>
          </CenterOverlay>
        )}

        {state.kind === "success" && (
          <BottomSheet>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {tm("successHeading", { count: countFields(state.parsed) })}
            </Typography>
            <SheetButton onClick={handleDone}>{tm("doneButton")}</SheetButton>
          </BottomSheet>
        )}

        {state.kind === "unrecognized" && (
          <BottomSheet>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {tm("unrecognizedHeading")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {tm("unrecognizedHelp")}
            </Typography>
            <SheetButton
              onClick={() => dispatch({ type: "TRY_AGAIN" })}
              style={{ marginBottom: 8 }}
            >
              {tm("tryAgainButton")}
            </SheetButton>
            <SheetButton variant="secondary" onClick={handleCancel}>
              {tm("fillManuallyButton")}
            </SheetButton>
          </BottomSheet>
        )}

        {state.kind === "unavailable" && (
          <BottomSheet>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {unavailableTitle(state.reason, tm)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {unavailableBody(state.reason, tm)}
            </Typography>
            <SheetButton onClick={handleCancel}>
              {tm("closeButton")}
            </SheetButton>
          </BottomSheet>
        )}
      </Box>
    </Dialog>
  );
};

// The viewfinder box, drawn in display space (a centered wide band). Because it
// uses the same VIEWFINDER fractions/aspect as sourceCropRect, the box you see is
// exactly the region the WASM decoder crops — and it stays landscape regardless
// of the camera's native orientation. (For the browser engine it's a
// conservative framing guide, since that engine decodes the full frame.)
const ReticleOverlay: FC = () => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Box
      sx={{
        width: `${VIEWFINDER.widthFraction * 100}%`,
        aspectRatio: `${VIEWFINDER.aspectRatio}`,
        maxHeight: "100%",
        border: "2px solid rgba(255,255,255,0.6)",
        borderRadius: 2,
        boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
      }}
    />
  </Box>
);

const CenterOverlay: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: alpha(colors.common.black, 0.45),
      pointerEvents: "none",
    }}
  >
    {children}
  </Box>
);

const TopBanner: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      position: "absolute",
      top: 64,
      left: 16,
      right: 16,
      p: 2,
      bgcolor: alpha(colors.common.black, 0.7),
      borderRadius: 1,
      textAlign: "center",
    }}
  >
    <Typography variant="body2">{children}</Typography>
  </Box>
);

// Native SurveyJS button — matches the scan-trigger button and survey chrome.
// `action` is the filled primary CTA (sd-btn--action); `secondary` is the
// lower-emphasis outline-style button (plain sd-btn). MUI's <Button> isn't used
// here because it can't pick up the `sd-btn` look the rest of the survey uses.
const SheetButton: FC<{
  children: ReactNode;
  onClick: () => void;
  variant?: "action" | "secondary";
  style?: CSSProperties;
}> = ({ children, onClick, variant = "action", style }) => (
  <button
    type="button"
    className={variant === "action" ? "sd-btn sd-btn--action" : "sd-btn"}
    onClick={onClick}
    style={{ display: "block", width: "100%", ...style }}
  >
    {children}
  </button>
);

const BottomSheet: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      p: 3,
      bgcolor: "background.paper",
      color: "text.primary",
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
    }}
  >
    {children}
  </Box>
);

const LockOnOverlay: FC<{
  points: ReadonlyArray<{ x: number; y: number }>;
  videoNaturalWidth?: number;
  videoNaturalHeight?: number;
}> = ({ points, videoNaturalWidth, videoNaturalHeight }) => {
  if (points.length < 2) {
    return (
      <CenterOverlay>
        <Box sx={{ fontSize: 64, color: colors.success.light }}>✓</Box>
      </CenterOverlay>
    );
  }

  // zxing returns ResultPoints in the video's natural-pixel coordinates,
  // so the SVG viewBox must match. preserveAspectRatio="xMidYMid slice"
  // mirrors the video's `object-fit: cover`, keeping the overlay aligned
  // even when the video is letterboxed/cropped to fit the modal.
  const w =
    videoNaturalWidth && videoNaturalWidth > 0 ? videoNaturalWidth : 1000;
  const h =
    videoNaturalHeight && videoNaturalHeight > 0 ? videoNaturalHeight : 1000;

  // Bounding box of the points. PDF417 ResultPoints come back in an order
  // that produces a self-intersecting polygon when joined directly, so we
  // draw the bounding rect instead — robust to point ordering.
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const pad = Math.min(w, h) * 0.01;
  const x = Math.max(0, minX - pad);
  const y = Math.max(0, minY - pad);
  const rectW = Math.min(w - x, maxX - minX + pad * 2);
  const rectH = Math.min(h - y, maxY - minY + pad * 2);
  const strokeWidth = Math.max(2, Math.min(w, h) * 0.008);

  return (
    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="xMidYMid slice"
        style={{ width: "100%", height: "100%" }}
      >
        <rect
          x={x}
          y={y}
          width={rectW}
          height={rectH}
          rx={pad * 2}
          fill="rgba(34,197,94,0.15)"
          stroke={colors.success.light}
          strokeWidth={strokeWidth}
          style={{
            animation: "scannerLockOn 600ms ease-out forwards",
          }}
        />
      </svg>
      <style>{`
        @keyframes scannerLockOn {
          0%   { opacity: 0; }
          50%  { opacity: 1; }
          100% { opacity: 1; }
        }
      `}</style>
    </Box>
  );
};

function unavailableTitle(
  reason: PermissionFailureReason,
  tm: (k: string) => string,
): string {
  switch (reason) {
    case "denied":
      return tm("unavailableDeniedHeading");
    case "not-found":
      return tm("unavailableNotFoundHeading");
    case "unsupported":
      return tm("unavailableUnsupportedHeading");
    case "other":
      return tm("unavailableOtherHeading");
  }
}

function unavailableBody(
  reason: PermissionFailureReason,
  tm: (k: string) => string,
): string {
  switch (reason) {
    case "denied":
      return tm("unavailableDeniedBody");
    case "not-found":
      return tm("unavailableNotFoundBody");
    case "unsupported":
      return tm("unavailableUnsupportedBody");
    case "other":
      return tm("unavailableOtherBody");
  }
}

function isParsedEmpty(parsed: ParsedLicense): boolean {
  const hasScalar =
    parsed.firstName ||
    parsed.lastName ||
    parsed.dob ||
    parsed.sex ||
    parsed.driverLicenseNo ||
    parsed.driverLicenseJurisdiction;
  return !hasScalar && !parsed.address;
}

function countFields(parsed: ParsedLicense): number {
  let n = 0;
  if (parsed.firstName) n++;
  if (parsed.lastName) n++;
  if (parsed.dob) n++;
  if (parsed.sex) n++;
  if (parsed.driverLicenseNo) n++;
  if (parsed.driverLicenseJurisdiction) n++;
  if (parsed.address) n++;
  return n;
}

function formatAddress(addr: ParsedLicense["address"]): string {
  if (!addr) return "";
  return [addr.street, addr.city, addr.province, addr.postalCode]
    .filter(Boolean)
    .join(", ");
}
